import { type RefObject, useState } from 'react';
import { Panels } from './CosmeticRepairRequestTypes';
import React from 'react';
import { All_PANELS } from './CosmeticHelper';

export type TooltipState = {
    left: number;
    top: number;
    text: string;
};

type UsePanelSelectorProps = {
    selectedPanels: Panels[];
    allowedPanels: Panels[];
    notAllowedSelectionMessage: string;
    containerRef: RefObject<HTMLDivElement>;
    onSelectionChange: (selected: boolean, panel: Panels) => void;
};

const usePanelSelector = ({
    selectedPanels,
    allowedPanels,
    notAllowedSelectionMessage,
    containerRef,
    onSelectionChange
}: UsePanelSelectorProps) => {
    const [forbidden, setForbidden] = useState<Panels | null>(null);
    const [notAllowedSelectionTooltip, setNotAllowedSelectionTooltip] =
        useState<TooltipState | null>(null);

    const isPanelAllowed = (panel: Panels) => {
        return allowedPanels.includes(panel);
    };

    const showNotAllowedTooltip = (event: React.MouseEvent<SVGPathElement>) => {
        const containerRect = containerRef.current?.getBoundingClientRect();

        setNotAllowedSelectionTooltip({
            left: event.clientX - containerRect.left + 10,
            top: event.clientY - containerRect.top + 10,
            text: notAllowedSelectionMessage
        });
    };

    const handleSelectionChange = (
        event: React.MouseEvent<SVGPathElement>,
        panel: Panels
    ) => {
        if (!isPanelAllowed(panel)) {
            showNotAllowedTooltip(event);
            setForbidden(panel);
            setTimeout(() => setForbidden(null), 1000);
            return;
        }

        const isSelected = selectedPanels.includes(panel);
        onSelectionChange(!isSelected, panel);
    };

    const handlePanelLeave = () => {
        setNotAllowedSelectionTooltip(null);
    };

    const getPanelClass = (panel: Panels) => {
        if (selectedPanels.includes(panel)) {
            return 'highlighted';
        }

        const isAllowed = isPanelAllowed(panel);
        const showAllowedPanels = allowedPanels.length < All_PANELS.length;

        return [
            'normal',
            forbidden === panel && 'pulse-danger',
            !isAllowed && 'not-allowed',
            showAllowedPanels &&
                selectedPanels.length > 0 &&
                isAllowed &&
                'allowed'
        ]
            .filter(Boolean)
            .join(' ');
    };

    return {
        getPanelClass,
        handlePanelLeave,
        handleSelectionChange,
        notAllowedSelectionTooltip
    };
};

export default usePanelSelector;
